/**
 * Reusable Card component for displaying content in a contained box
 */
import React, { ReactNode } from 'react';

/**
 * Props for the Card component
 */
interface CardProps {
  /**
   * Card title
   */
  title?: ReactNode;
  
  /**
   * Card content
   */
  children: ReactNode;
  
  /**
   * Optional footer content
   */
  footer?: ReactNode;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Whether to add a hover effect
   */
  hoverable?: boolean;
  
  /**
   * Optional header actions (e.g., buttons)
   */
  headerActions?: ReactNode;
}

/**
 * A card component for displaying content in a contained box with optional title and footer
 */
export function Card({
  title,
  children,
  footer,
  className = '',
  hoverable = false,
  headerActions
}: CardProps) {
  return (
    <div 
      className={`
        bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden
        ${hoverable ? 'transition-shadow hover:shadow-md' : ''}
        ${className}
      `}
    >
      {title && (
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          {typeof title === 'string' ? (
            <h3 className="font-medium text-gray-800">{title}</h3>
          ) : (
            title
          )}
          
          {headerActions && (
            <div className="flex items-center space-x-2">
              {headerActions}
            </div>
          )}
        </div>
      )}
      
      <div className="p-4">
        {children}
      </div>
      
      {footer && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          {footer}
        </div>
      )}
    </div>
  );
}
